import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom'
import { Code, Droplets, ExternalLink, Menu, X } from 'lucide-react'
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import WaterLevel from './components/WaterLevel'
import DeveloperGuide from './pages/DeveloperGuide'
import PrivacyPolicy from './pages/PrivacyPolicy'
import EmbeddableWaterLevelWidget from './components/EmbeddableWaterLevelWidget'
import Footer from './components/Footer'
import LanguageSwitcher from './components/LanguageSwitcher'
import ThemeToggle from './components/ThemeToggle'
import { ThemeContext, useThemeState } from './hooks/useTheme'

// Extract URL parameters for the embed route
const EmbedPage = () => {
  const urlParams = new URLSearchParams(window.location.search)
  
  const props = {
    compact: urlParams.get('compact') === 'true',
    theme: (urlParams.get('theme') as 'light' | 'dark') || 'light',
    showRainfall: urlParams.get('rainfall') !== 'false',
    showCctv: urlParams.get('cctv') === 'true',
    hideHeader: urlParams.get('hideHeader') === 'true',
    width: urlParams.get('width') || 'auto',
    height: urlParams.get('height') || 'auto'
  }
  

  
  return (
    <div className="p-4">
      <EmbeddableWaterLevelWidget {...props} />
    </div>
  )
}

// Main App Layout Component
const AppLayout = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const isEmbedPage = location.pathname.includes('/embed')
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
  }

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false)
  }

  // Close mobile menu on route change
  useEffect(() => {
    closeMobileMenu()
  }, [location.pathname])

  // Close mobile menu when window is resized to desktop size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        closeMobileMenu()
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const nav = document.querySelector('nav')
      if (isMobileMenuOpen && nav && !nav.contains(event.target as Node)) {
        closeMobileMenu()
      }
    }

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      // Prevent body scroll when mobile menu is open
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.body.style.overflow = 'unset'
    }
  }, [isMobileMenuOpen])

  return (
    <div className="App min-h-screen flex flex-col bg-white dark:bg-gray-900 transition-colors">
      {/* Navigation Header - Hide on embed pages */}
      {!isEmbedPage && (
        <nav className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 py-3">
            {/* Desktop Navigation */}
            <div className="flex items-center justify-between">
              {/* Logo */}
              <Link 
                to="/" 
                className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                onClick={closeMobileMenu}
              >
                <Droplets className="h-6 w-6" />
                <span className="font-semibold text-lg">{t('navigation.siteTitle')}</span>
              </Link>
              
              {/* Desktop Navigation Links */}
              <div className="hidden md:flex items-center space-x-8">
                <div className="flex items-center space-x-6">
                  <Link
                    to="/"
                    className="text-gray-700 hover:text-blue-600 dark:text-gray-200 dark:hover:text-blue-400 font-medium transition-colors"
                  >
                    {t('navigation.waterLevel')}
                  </Link>
                  <Link
                    to="/developer-guide"
                    className="flex items-center space-x-1 text-gray-700 hover:text-blue-600 dark:text-gray-200 dark:hover:text-blue-400 font-medium transition-colors"
                  >
                    <Code className="h-4 w-4" />
                    <span>{t('navigation.developerGuide')}</span>
                  </Link>
                  <Link
                    to="/privacy-policy"
                    className="text-gray-700 hover:text-blue-600 dark:text-gray-200 dark:hover:text-blue-400 font-medium transition-colors"
                  >
                    {t('navigation.privacy')}
                  </Link>
                </div>
                
                <div className="flex items-center space-x-4">
                  <LanguageSwitcher />
                  <ThemeToggle />
                  <button
                    onClick={() => window.open('/embed', '_blank', 'noopener,noreferrer')}
                    className="flex items-center space-x-1 text-gray-600 hover:text-blue-600 dark:text-gray-300 dark:hover:text-blue-400 text-sm transition-colors"
                  >
                    <ExternalLink className="h-4 w-4" />
                    <span>{t('navigation.viewEmbed')}</span>
                  </button>
                </div>
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={toggleMobileMenu}
                className="md:hidden p-2 rounded-md text-gray-600 hover:text-blue-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:text-blue-400 dark:hover:bg-gray-700 transition-colors"
                aria-label="Toggle mobile menu"
              >
                {isMobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>

            {/* Mobile Navigation Menu */}
            <div className={`md:hidden transition-all duration-300 ease-in-out ${
              isMobileMenuOpen 
                ? 'max-h-80 opacity-100 pt-4' 
                : 'max-h-0 opacity-0 overflow-hidden'
            }`}>
              <div className="border-t border-gray-200 dark:border-gray-600 pt-4 space-y-4">
                <Link
                  to="/"
                  className="block text-gray-700 hover:text-blue-600 dark:text-gray-200 dark:hover:text-blue-400 font-medium transition-colors py-2 px-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                  onClick={closeMobileMenu}
                >
                  {t('navigation.waterLevel')}
                </Link>
                
                <Link
                  to="/developer-guide"
                  className="flex items-center space-x-2 text-gray-700 hover:text-blue-600 dark:text-gray-200 dark:hover:text-blue-400 font-medium transition-colors py-2 px-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                  onClick={closeMobileMenu}
                >
                  <Code className="h-4 w-4" />
                  <span>{t('navigation.developerGuide')}</span>
                </Link>
                <Link
                  to="/privacy-policy"
                  className="block text-gray-700 hover:text-blue-600 dark:text-gray-200 dark:hover:text-blue-400 font-medium transition-colors py-2 px-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                  onClick={closeMobileMenu}
                >
                  {t('navigation.privacy')}
                </Link>
                
                <button
                  onClick={() => {
                    window.open('/embed', '_blank', 'noopener,noreferrer')
                    closeMobileMenu()
                  }}
                  className="flex items-center space-x-2 text-gray-600 hover:text-blue-600 dark:text-gray-300 dark:hover:text-blue-400 transition-colors py-2 px-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 w-full text-left"
                >
                  <ExternalLink className="h-4 w-4" />
                  <span>{t('navigation.viewEmbed')}</span>
                </button>
                
                <div className="pt-2 flex items-center justify-between">
                  <LanguageSwitcher />
                  <ThemeToggle />
                </div>
              </div>
            </div>
          </div>
        </nav>
      )}

      {/* Main Content */}
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<WaterLevel />} />
          <Route path="/developer-guide" element={<DeveloperGuide />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/embed" element={<EmbedPage />} />
        </Routes>
      </main>

      {/* Footer - Hide on embed pages */}
      {!isEmbedPage && <Footer />}
    </div>
  )
}

function App() {
  const themeState = useThemeState()
  
  return (
    <ThemeContext.Provider value={themeState}>
      <Router>
        <AppLayout />
      </Router>
    </ThemeContext.Provider>
  )
}

export default App 